import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { useLogout } from '../lib/api'
import { useWsEvents } from '../lib/ws'
import { useTheme } from '../lib/theme'
import {
  IconDashboard, IconChallenge, IconSportsbook, IconBets, IconLeaderboard,
  IconBilling, IconHelp, IconSettings, IconSun, IconMoon, IconBell, IconUser, IconSignOut,
} from './Icons'

export type PageKey =
  | 'dashboard' | 'challenge' | 'sportsbook' | 'bets' | 'leaderboard'
  | 'billing' | 'help' | 'settings' | 'kyc' | 'profile' | 'notifications'

interface NavSpec { to: string; page: PageKey; label: string; Icon: typeof IconDashboard; badge?: string | null }

interface AppShellProps {
  page?: PageKey
  breadcrumb?: ReactNode
  tierBadge?: ReactNode
  showBalance?: boolean
  children: ReactNode
}

function fmtBalance(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-IE', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n)
}

export function AppShell({ page = 'dashboard', breadcrumb, tierBadge, showBalance = true, children }: AppShellProps) {
  useWsEvents()
  const { user, account } = useAuth()
  const logout = useLogout()
  const nav = useNavigate()
  const { toggle } = useTheme()

  const initials = (user?.displayName ?? user?.email ?? '?')
    .split(/[\s@]+/).map((s) => s.charAt(0).toUpperCase()).filter(Boolean).slice(0, 2).join('')

  const navItems: NavSpec[] = [
    { to: '/', page: 'dashboard', label: 'Dashboard', Icon: IconDashboard },
    { to: '/challenge', page: 'challenge', label: 'Challenge', Icon: IconChallenge, badge: account ? account.currentPhase.toUpperCase() : null },
    { to: '/sportsbook', page: 'sportsbook', label: 'Sportsbook', Icon: IconSportsbook },
    { to: '/bets', page: 'bets', label: 'My Bets', Icon: IconBets },
    { to: '/leaderboard', page: 'leaderboard', label: 'Leaderboard', Icon: IconLeaderboard },
    { to: '/billing', page: 'billing', label: 'Billing', Icon: IconBilling },
  ]
  const accountNav: NavSpec[] = [
    { to: '/help', page: 'help', label: 'Help & Support', Icon: IconHelp },
    { to: '/settings', page: 'settings', label: 'Settings', Icon: IconSettings },
  ]

  return (
    <div className="app" data-page={page}>
      <aside className="app__sidebar sidebar" aria-label="Primary navigation">
        <Link to="/" className="sidebar__logo" aria-label="Rival Prop Firm">
          <img src="/wordmark-light.svg" alt="Rival Prop Firm" className="sidebar__logo-img sidebar__logo-img--full-light" />
          <img src="/wordmark-dark.svg"  alt="Rival Prop Firm" className="sidebar__logo-img sidebar__logo-img--full-dark" />
          <img src="/icon-light.svg" alt="" className="sidebar__logo-img sidebar__logo-img--square-light" aria-hidden />
          <img src="/icon-dark.svg"  alt="" className="sidebar__logo-img sidebar__logo-img--square-dark"  aria-hidden />
        </Link>

        <nav className="sidebar__section" aria-label="App sections">
          {navItems.map(({ to, page: pk, label, Icon, badge }) => (
            <Link key={pk} to={to} className="nav-item" data-nav={pk}>
              <Icon className="nav-item__icon" />
              <span className="nav-item__label">{label}</span>
              {badge && <span className="nav-item__badge">{badge}</span>}
            </Link>
          ))}
        </nav>

        <div className="sidebar__spacer" />

        <nav className="sidebar__section" aria-label="Account">
          {accountNav.map(({ to, page: pk, label, Icon }) => (
            <Link key={pk} to={to} className="nav-item" data-nav={pk}>
              <Icon className="nav-item__icon" />
              <span className="nav-item__label">{label}</span>
            </Link>
          ))}
        </nav>
      </aside>

      <header className="app__topbar topbar">
        <div className="topbar__breadcrumb">
          {breadcrumb ?? <span className="topbar__breadcrumb-current">Dashboard</span>}
        </div>
        <div className="topbar__spacer" />

        {/* Tier badge: explicit prop wins (richer composition in PhaseView),
            otherwise fall back to a global badge derived from the auth context
            so every page surfaces the user's active tier + phase. */}
        {tierBadge ?? (account ? (
          <span className="tier-badge tier-badge--active">
            <span className="tier-badge__dot" aria-hidden />
            {account.tierCode.charAt(0).toUpperCase() + account.tierCode.slice(1)} Challenge · {account.currentPhase.toUpperCase()}
          </span>
        ) : null)}

        {showBalance && account && (
          <div className="balance-pill">
            <span className="balance-pill__label">Balance</span>
            <span className="balance-pill__value">{fmtBalance(account.currentBalance)}</span>
          </div>
        )}

        <button type="button" className="theme-toggle" aria-label="Toggle theme" onClick={toggle}>
          <IconSun  className="theme-toggle__icon theme-toggle__icon--sun" />
          <IconMoon className="theme-toggle__icon theme-toggle__icon--moon" />
        </button>

        <Link to="/notifications" className="icon-btn" aria-label="Notifications" style={{ position: 'relative', textDecoration: 'none' }}>
          <IconBell />
          <span aria-hidden style={{ position: 'absolute', top: 4, right: 6, width: 8, height: 8, borderRadius: 999, background: 'var(--color-danger)', border: '2px solid var(--bg-canvas)' }} />
        </Link>

        <details className="profile-menu">
          <summary className="avatar" aria-label="Account menu" role="button">{initials || '?'}</summary>
          <div className="profile-menu__panel" role="menu">
            <div className="profile-menu__header">
              <span className="profile-menu__name">{user?.displayName ?? 'Trader'}</span>
              <span className="profile-menu__email">{user?.email ?? ''}</span>
            </div>
            <Link to="/profile" className="profile-menu__option" role="menuitem">
              <IconUser /> Profile
            </Link>
            <Link to="/settings" className="profile-menu__option" role="menuitem">
              <IconSettings /> Settings
            </Link>
            <div className="profile-menu__divider" />
            <button
              type="button"
              className="profile-menu__option profile-menu__option--danger"
              role="menuitem"
              onClick={() => { logout.mutate(undefined, { onSuccess: () => nav('/login') }) }}
            >
              <IconSignOut /> Sign out
            </button>
          </div>
        </details>
      </header>

      <main className="app__main main" id="main-content">
        {children}
      </main>
    </div>
  )
}
